import Service, { inject as service } from '@ember/service';
import { isBlank, isNone, isEmpty } from '@ember/utils';
import config from 'cio/config/environment';
import { timeout } from 'ember-concurrency';
import { task, dropTask } from 'ember-concurrency';
import ApiService from 'cio/services/api';
import FeaturesService from 'ember-feature-flags/services/features';
import { CampaignState, CampaignType, TYPE_ICONS } from 'cio/models/campaign';
import { Channel } from 'cio/utils/metrics/common';
import ApiErrorsService from './api-errors';
import { tracked } from '@glimmer/tracking';
import { unixNowSeconds } from 'cio/utils/time';
import { sortByCaseInsensitive } from 'cio/utils/sort';

const { filtersThrottle } = config.APP;

export type FilterMessage = {
  action_id: string;
  campaign_id: string;
  can_segment_on_variation: boolean;
  draftable?: boolean;
  is_segmentable_variation?: boolean;
  newsletter_id: string;
  output_id: string;
  parent?: {
    id: string;
    type: string;
    name: string;
    campaignType?: CampaignType;
    campaignFirstStarted?: number | null;
  };
  searchLabel?: string;
  subject: string;
  sort_value?: string;
  template_id: string;
  transactional_message_id: string;
  type: Channel;
  variation: number | null;
  language?: string | null;
  isSplit?: boolean;
  isLanguageBranch?: boolean;
};

export type FilterModel = {
  id: string;
  name: string;
};

export type CampaignFilterModel = FilterModel & {
  type: CampaignType;
  state: CampaignState;
  value?: string;
  first_started: number | null; // timestamp
};

export type TransactionalMessageFilterModel = FilterModel & {
  type: Channel;
};

const STALE_THRESHOLD_SECONDS = 60;

function itemsArrayToMap<T extends { id: string }>(items: T[]): Map<string, T>;
function itemsArrayToMap<T extends { id: string }, U>(items: T[], transformer: (item: T) => U): Map<string, U>;
function itemsArrayToMap<T extends { id: string }, U>(items: T[], transformer?: (item: T) => U): Map<string, T | U> {
  return items.reduce((map, item) => {
    map.set(item.id, transformer != null ? transformer(item) : item);
    return map;
  }, new Map<string, T | U>());
}

export default class FiltersService extends Service {
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service features!: FeaturesService;

  campaigns: CampaignFilterModel[] = [];
  newsletters: FilterModel[] = [];
  transactionalMessages: TransactionalMessageFilterModel[] = [];

  messages: FilterMessage[] = [];
  segmentable: FilterMessage[] = [];
  branchMessageTypes: Record<string, string> = {};

  @tracked lastFetchTimestamp = 0;

  processIfEmptyOrStale = task(async (staleThreshold = STALE_THRESHOLD_SECONDS) => {
    let now = unixNowSeconds();

    if (this.lastFetchTimestamp === 0 || now - this.lastFetchTimestamp >= staleThreshold) {
      this.process.perform();
    }
  });

  process = task(async () => {
    // If the processTask is running, we don't want to enqueue it again.
    //
    // It will get dropped and if the task is being used as a promise, i.e.
    // with a "then", it will throw an error. It's fine if the error is expected
    // and handled, but this breaks within a route's lifecycle hooks.
    //
    // For example, if `processTask` is running and a route's afterModel tries
    // to create it again, the task will be dropped and an error will be thrown
    // which will lead to a broken app state.
    if (this.processTask.isRunning) {
      return await this.processTask.last;
    }

    return await this.processTask.perform();
  });

  request = dropTask(async () => {
    try {
      return await this.api.request('deliveries/filters', { ignoreErrors: true });
    } catch (e) {
      this.apiErrors.report(e);

      return {
        campaigns: [],
        newsletters: [],
        messages: [],
        segmentable: [],
        transactional_messages: [],
        branch_messages: [],
      };
    }
  });

  fetch = dropTask(async () => {
    await this.process.perform();
    await timeout(filtersThrottle);
  });

  processTask = dropTask(async () => {
    let filters = await this.request.perform();
    let {
      campaigns,
      newsletters,
      messages,
      transactional_messages: transactionalMessages,
      branch_messages: branchMessages,
    }: {
      campaigns: CampaignFilterModel[];
      newsletters: FilterModel[];
      transactional_messages: TransactionalMessageFilterModel[];
      branch_messages: FilterMessage[];
      messages: FilterMessage[];
    } = filters;

    // Prepare id <> entry maps for parent items
    let campaignsMap = itemsArrayToMap(campaigns, (campaign) => ({
      id: String(campaign.id),
      type: campaign.type === CampaignType.APITriggered ? 'broadcast' : 'campaign',
      campaignTypeIcon: TYPE_ICONS[campaign.type as CampaignType],
      name: campaign.name,
      campaignType: campaign.type as CampaignType,
      campaignFirstStarted: campaign.first_started,
    }));
    let newslettersMap = itemsArrayToMap(newsletters, (newsletter) => ({
      id: String(newsletter.id),
      type: 'newsletter',
      name: newsletter.name,
    }));
    let transactionalMessagesMap = itemsArrayToMap(transactionalMessages, (transactionalMessage) => ({
      id: String(transactionalMessage.id),
      type: 'transactional message',
      name: transactionalMessage.name,
    }));

    function findParent(message: FilterMessage) {
      let maybeCampaign = campaignsMap.get(message.campaign_id);
      if (maybeCampaign != null) {
        return maybeCampaign;
      }

      let maybeNewsletter = newslettersMap.get(message.newsletter_id);
      if (maybeNewsletter != null) {
        return maybeNewsletter;
      }

      let maybeTransactionalMessage = transactionalMessagesMap.get(message.transactional_message_id);
      if (maybeTransactionalMessage != null) {
        return maybeTransactionalMessage;
      }

      return undefined;
    }

    this.branchMessageTypes = {};

    branchMessages = (branchMessages ?? []).map((message: FilterMessage) => {
      let parent = findParent(message);
      let sortValue = message.subject;
      let searchLabel = `${message.subject ?? ''} ${parent?.name ?? ''} All Variations`.trim();
      let subject = `${message.subject ?? ''} - All Variations`;

      this.branchMessageTypes[message.output_id] = message.type;

      return {
        isLanguageBranch: true,
        draftable: true,
        parent,
        searchLabel,
        sort_value: sortValue,
        ...message,
        subject,
      };
    });

    messages = messages.map((message: FilterMessage): FilterMessage => {
      let parent = findParent(message);
      let draftable = isNone(message.newsletter_id);
      let searchLabel = `${message.subject ?? ''} ${parent?.name ?? ''}`.trim();
      // `can_segment_on_variation` is a boolean that indicates that the message was sent
      // after we began tracking the template_id for segmentation.
      // Segmenting on variations is currently only allowed for newsletters
      let isSegmentableVariation = parent?.type === 'newsletter' && message.can_segment_on_variation;
      let sortValue = isSegmentableVariation ? parent!.name : message.subject;

      return {
        draftable,
        parent,
        searchLabel,
        sort_value: sortValue,
        is_segmentable_variation: isSegmentableVariation,
        ...message,
      };
    });

    messages = sortByCaseInsensitive([...messages, ...branchMessages], ['sort_value']);

    let addedAllVariationsOutputIds = new Set<string>();

    this.setProperties({
      campaigns: sortByCaseInsensitive(campaigns.map(campaignItem), ['label']),
      newsletters: sortByCaseInsensitive(newsletters.map(newsletterItem), ['label']),
      transactionalMessages: sortByCaseInsensitive(transactionalMessages.map(transactionalItem), ['label']),
      messages,
      segmentable: messages.reduce((messagesAgg: FilterMessage[], message) => {
        let { is_segmentable_variation: isSegmentableVariation } = message;

        // if this is the first of a set of segmentable Newsletter variations, we need to create an option for
        // "all variations" as well as for each variation.
        if (isSegmentableVariation && (isFirstVariation(message) || !isEmpty(message.language))) {
          let splitOutputId = message.output_id.split('_').filter((seg) => seg !== 'variation');
          let outputId =
            message.type === 'email'
              ? `${splitOutputId[0]}_${splitOutputId[1]}` // newsletter_:newsletterId
              : `${splitOutputId[0]}_${splitOutputId[1]}_${splitOutputId[2]}`; // :channel_newsletter_:newsletterId

          if (!addedAllVariationsOutputIds.has(outputId)) {
            let combinedMessage: FilterMessage = {
              ...message,
              output_id: outputId,
              is_segmentable_variation: false,
              subject: `${message.subject} - All Variations`,
              isSplit: true,
            };
            delete combinedMessage.language;

            messagesAgg = [...messagesAgg, combinedMessage];
            addedAllVariationsOutputIds.add(outputId);
          }
        }

        if ((isSegmentableVariation ?? false) || isPrimary(message)) {
          let messageCopy = {
            ...message,
            isSplit: !isSegmentableVariation && !isNone(message.variation),
          };
          if (messageCopy.isSplit) {
            messageCopy.subject = `${messageCopy.subject} - All Variations`;
          }
          messagesAgg = [...messagesAgg, messageCopy];
        }

        return messagesAgg;
      }, []),
    });

    this.lastFetchTimestamp = unixNowSeconds();
  });

  isBranchMessage(outputId: string | null) {
    if (outputId == null) {
      return false;
    }

    return this.branchMessageTypes[outputId] != null;
  }
}

function isFirstVariation({ variation }: FilterMessage) {
  return variation === 1;
}

function isPrimary(message: FilterMessage) {
  return isNone(message.variation) || isFirstVariation(message);
}

function nonBlankName(name: string) {
  return isBlank(name) ? '(Untitled)' : name;
}

function campaignItem(campaign: CampaignFilterModel) {
  return {
    icon: campaign.type === CampaignType.APITriggered ? 'broadcast' : 'campaigns',
    campaignTypeIcon: TYPE_ICONS[campaign.type as CampaignType],
    label: nonBlankName(campaign.name),
    value: campaign.id,
    firstStarted: campaign.first_started,
    ...campaign,
  };
}

function newsletterItem(newsletter: FilterModel) {
  return {
    icon: 'broadcast',
    label: nonBlankName(newsletter.name),
    value: `newsletter_${newsletter.id}`,
    ...newsletter,
  };
}

function transactionalItem(message: TransactionalMessageFilterModel) {
  return {
    icon: 'transactional-api',
    label: nonBlankName(message.name),
    value: `transactional_${message.id}`,
    ...message,
  };
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    filters: FiltersService;
  }
}
